import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleSeo } from '../../types/articleGeneration';

interface Props {
  seo: ArticleSeo | null | undefined;
}

export const TITLE_MIN = 45;
export const TITLE_MAX = 55;
export const META_MAX = 155;

const tone = (ok: boolean, warn = false) =>
  ok ? 'text-green-700' : warn ? 'text-amber-700' : 'text-red-700';

/**
 * The search metadata suggestions above a generated artifact: the page title
 * (brand suffix included), the meta description of a full article, and the H1.
 */
const SeoBlock: React.FC<Props> = ({ seo }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState<string | null>(null);
  if (!seo) return null;

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // The row of export buttons offers the same text; a failed copy here is silent.
    }
  };

  const titleLen = seo.title.length;
  const titleOk = titleLen >= TITLE_MIN && titleLen <= TITLE_MAX;
  const metaLen = seo.metaDescription?.length ?? 0;
  const metaOk = metaLen <= META_MAX;

  const row = (
    key: 'title' | 'meta' | 'h1',
    label: string,
    value: string,
    count: React.ReactNode
  ) => (
    <div className="grid grid-cols-[9rem_1fr_auto] gap-x-3 gap-y-0.5 items-start" data-testid={`seo-${key}`}>
      <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 pt-0.5">{label}</dt>
      <dd className="text-sm text-gray-900 break-words">
        {value}
        {count && <div className="text-xs mt-0.5">{count}</div>}
      </dd>
      <button
        type="button"
        onClick={() => copy(key, value)}
        className="text-xs text-blue-600 hover:underline"
        aria-label={`${t('article.seo_copy')} ${label}`}
      >
        {copied === key ? t('article.seo_copied') : t('article.seo_copy')}
      </button>
    </div>
  );

  return (
    <section
      data-testid="seo-block"
      className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
      aria-label={t('article.seo_block_title')}
    >
      <h3 className="text-sm font-semibold text-gray-800 mb-2">{t('article.seo_block_title')}</h3>
      <dl className="space-y-2">
        {row(
          'title',
          t('article.seo_title_label'),
          seo.title,
          <span className={tone(titleOk, true)}>
            {t('article.seo_chars', { count: titleLen })} · {t('article.seo_title_hint')}
          </span>
        )}
        {seo.metaDescription &&
          row(
            'meta',
            t('article.seo_meta_label'),
            seo.metaDescription,
            <span className={tone(metaOk)}>
              {t('article.seo_chars', { count: metaLen })} · {t('article.seo_meta_hint')}
            </span>
          )}
        {row('h1', t('article.seo_h1_label'), seo.h1, null)}
      </dl>
    </section>
  );
};

export default SeoBlock;
